import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { ParcsOrganizations } from 'src/app/shared/code/constants-enums/lanai-enums';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { IAmanoOneFacilityResponse } from 'src/app/shared/code/interfaces/parcs-interfaces';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { ProcessingBannerComponent } from 'src/app/shared/ui/processing-banner/processing-banner.component';
import { ErrorBannerComponent } from 'src/app/shared/ui/error-banner/error-banner.component';
import { AceDialogs } from 'src/app/core/code/helpers/ace-dialogs';

@Component({
  selector: 'app-amano-one',
  standalone: true,
  imports: [
    MatCardModule,
    FormsModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatInputModule,
    MatOptionModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatButtonModule,
    MatSelectModule,
    MatButtonToggleModule,
    ProcessingBannerComponent,
  ],
  templateUrl: './amano-one.component.html',
  styleUrl: './amano-one.component.scss',
})
export class AmanoOneComponent implements OnInit {
  @ViewChild('stepper') private parcsStepper: MatStepper;

  isValidationState: boolean = true;
  parcsList: string[] = this.getParkcsListFromEnum();
  selectedPARCS: string = '';
  selectedFacilityId: string = '';
  selectedFacilityName: string = '';
  isActivationInProgress: boolean = true;
  _selectedParcsId: string = '';
  _selectedParcsName: string = '';
  parcsValidationData: IAmanoOneFacilityResponse = {} as IAmanoOneFacilityResponse;
  lprStatusText: string = '';
  faclityIdError: boolean = false;
  isProcessing: boolean = false;
  processingMessage: string = '';
  statusLprEnabled: boolean = false;

  @Input()
  set parcsId(value: string) {
    if (value && value.length > 0) {
      this._selectedParcsId = value;
    }
  }

  @Input()
  set parcsName(value: string) {
    if (value && value.length > 0) {
      this._selectedParcsName = value;
    }
  }

  //Services
  _appGeneralState = inject(AppGeneralStateService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _parcsDataService = inject(ParcsDataService);
  _destroyRefService = inject(DestroyRef);
  private readonly _messageDialogService = inject(MessageDialogService);
  private aceDialogs = inject(AceDialogs);

  readonly parcsEntryForm = this._formBuilder.group({
    facilityId: '',
    lprIsPrimary: 'lprDisabled',
  });

  parcsReviewForm = this._formBuilder.group({
    facilityId: ['', Validators.required],
    facilityName: ['', Validators.required],
  });

  parcsListForm = this._formBuilder.group({
    parcName: ['', Validators.required],
  });

  ngOnInit(): void {}

  // resetAmanoForm(): void {
  // this.amanoForm = new FormGroup({
  //   isActive: new FormControl('', []),
  //   facilityId: new FormControl('', [Validators.required]),
  //   entityId: new FormControl('', [Validators.required]),
  //   lprIsPrimary: new FormControl('', []),
  // });
  // }

  // onSubmitAmanoData(): void {}

  onSubmitReviewAmanoData(): void {
    this.isValidationState = false;
  }

  onCancelSubmitValus(): void {
    this.isValidationState = true;
  }

  getParkcsListFromEnum(): string[] {
    return Object.values(ParcsOrganizations);
  }

  onParcsStepOne(): void {
    this.validateParcsFacilityExists(this.parcsEntryForm.get('facilityId')?.value?.trim() || '');
  }

  onParcsStepTwo(): void {
    var lprStatus = this.parcsEntryForm.get('lprIsPrimary')?.value;
    this.lprStatusText = lprStatus === 'lprEnabled' ? 'Yes' : 'No';
    this.statusLprEnabled = lprStatus === 'lprEnabled';
  }

  onParcsSuccessfullyValidated(): void {
    this.selectedFacilityId = this.parcsEntryForm.get('facilityId')?.value?.trim() || '';
    this.parcsReviewForm.setValue({
      facilityId: this.selectedFacilityId,
      facilityName: this.parcsValidationData.facilityName,
    });
    this.parcsReviewForm.updateValueAndValidity();
    this.parcsStepper.next();
  }

  onSubmitAmanoOneFacility(): void {
    const newLotFacilityData = {
      parcsId: this._selectedParcsId,
      lotNumber: this._appGeneralState.currentLotNumber,
      startDate: this.getCurrentLocalDateWithNoTimeComponent(), // 20260702 :: Ken Moss - added to insure we are using local date instead of utc date
      endDate: undefined,
      settings: `{"lotNumber":"${this._appGeneralState.currentLotNumber}","facilityId":"${this.parcsEntryForm.get('facilityId')?.value?.trim() || ''}","lprEnabled":${this.parcsEntryForm.get('lprIsPrimary')?.value === 'lprEnabled'}, "status":1, "mode": 1, "credentialType":1, "credentialSystemCode":"1","cardNumberPrefix":""}`,
      facilityId: this.parcsEntryForm.get('facilityId')?.value?.trim() || '',
    };

    this.isProcessing = true;
    this.processingMessage = `Adding PARCS Amano One Configuration for Lot ${this._appGeneralState.currentLotNumber}.`;

    this._parcsDataService
      .addParcsLotFacilityConfiguration(newLotFacilityData)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.isProcessing = false;
          this._messageDialogService.snackBarTopCenterTimed('Facility added successfully.', 3000);
          this.parcsStepper.next();
          this.onSetParcsConfiguredToFinished();
        },
        error: (err) => {
          this.isProcessing = false;
          this.aceDialogs.showErrorDialog(
            err?.error || 'An error occurred while adding the PARCS facility for this lot.',
            '',
            `Response Code: ${err.status}` || 'Unknown Error',
          );
        },
      });
  }

  // Returns the current local date as an ISO-8601 date string with no time component
  // (e.g. "2026-07-02"), so it is NOT converted to UTC on serialization.
  private getCurrentLocalDateWithNoTimeComponent(): string {
    const now = new Date();
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }

  onSetParcsConfiguredToFinished() {
    this.isActivationInProgress = false;
  }

  validateParcsFacilityExists(facilityId: string): void {
    this.processingMessage = 'Validating Facility Id...';
    this.isProcessing = true;

    this._parcsDataService
      .getAmanoOneFacilityNameByFacilityId(facilityId)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.parcsValidationData = res;
          this._selectedParcsName = this.parcsValidationData.facilityName;
        },
        error: (err: any) => {
          this.faclityIdError = true;
          this.isProcessing = false;
          this._messageDialogService.snackBarTopCenterTimed('Facility not found. Please check the Facility ID.', 3000);
        },
        complete: () => {
          this.faclityIdError = false;
          this.isProcessing = false;
          this._messageDialogService.snackBarTopCenterTimed('The Facility Id has been validated successfully', 3000);
          this.onParcsSuccessfullyValidated();
        },
      });
  }
}
